import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { error: Error | null };

// Last line of defence: if a lazily loaded page still can't be fetched after a
// retry, show a short message with a way back instead of a blank screen.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Failed to render:", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24 bg-background">
        <div className="max-w-md text-center space-y-6">
          <h1 className="text-2xl text-foreground leading-tight">
            This part didn't load
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            A piece of the page couldn't be fetched. Reloading usually brings it
            back.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm text-foreground transition-colors hover:bg-secondary"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
